import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  Slider,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  LinearProgress,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import HubIcon from "@mui/icons-material/Hub";
import dayjs from "dayjs";
import { SimulationParams } from "../contexts/SimulationContext";
import {
  EnsembleConfig,
  MAX_ENSEMBLE_IGNITIONS,
  maxEnsembleMembers,
  defaultEnsembleConfig,
} from "../types/ensemble";
import { SPOTTING_MODE_LABELS } from "../types/spotting";
import { weatherGridShapeLabel } from "../utils/weatherGrid";

type PertKey = "weather" | "fuel" | "topography";

interface Props {
  open: boolean;
  /** Number of ignition points in the selected regime — drives the member cap. */
  ignitionCount: number;
  regimeName?: string | null;
  /** Weather params already chosen in the weather modal, shown as a summary. */
  baseParams?: SimulationParams | null;
  onClose: () => void;
  /** Called with the ensemble config, or null for a plain single run. */
  onConfirm: (config: EnsembleConfig | null) => void;
  submitting?: boolean;
}

const STRENGTH_PRESETS: Record<string, number> = { low: 3, med: 8, high: 15 };

const PERT_META: { key: PertKey; title: string; desc: string }[] = [
  {
    key: "weather",
    title: "Weather",
    desc: "Gaussian noise on wind speed, wind direction, temperature & relative humidity at each timestep.",
  },
  {
    key: "fuel",
    title: "Fuel",
    desc: "Varies fuel load and grass curing across the landscape, cell by cell.",
  },
  {
    key: "topography",
    title: "Topography",
    desc: "Jitters slope & aspect derived from the DEM to reflect terrain uncertainty.",
  },
];

const EnsembleConfigModal: React.FC<Props> = ({
  open,
  ignitionCount,
  regimeName,
  baseParams,
  onClose,
  onConfirm,
  submitting = false,
}) => {
  const safeIgnitionCount = Math.max(1, ignitionCount || 1);
  const maxMembers = useMemo(
    () => maxEnsembleMembers(safeIgnitionCount),
    [safeIgnitionCount]
  );
  const capped = maxMembers === 1; // regime too large to ensemble

  const [config, setConfig] = useState<EnsembleConfig>(() =>
    defaultEnsembleConfig(safeIgnitionCount)
  );

  // Re-seed and clamp whenever the modal is (re)opened for a new regime.
  useEffect(() => {
    if (open) {
      setConfig(defaultEnsembleConfig(safeIgnitionCount));
    }
  }, [open, safeIgnitionCount]);

  const members = Math.min(config.members, maxMembers);
  const isEnsemble = config.enabled && !capped;
  const effectiveMembers = isEnsemble ? members : 1;
  const totalIgnitions = safeIgnitionCount * effectiveMembers;
  const budgetPct = Math.min(100, (totalIgnitions / MAX_ENSEMBLE_IGNITIONS) * 100);

  const setEnsembleEnabled = (enabled: boolean) =>
    setConfig((c) => ({ ...c, enabled }));

  const setMembers = (n: number) =>
    setConfig((c) => ({ ...c, members: Math.max(1, Math.min(n, maxMembers)) }));

  const setStrength = (s: keyof typeof STRENGTH_PRESETS) =>
    setConfig((c) => {
      const v = STRENGTH_PRESETS[s];
      const next = { ...c, perturbations: { ...c.perturbations } };
      (Object.keys(next.perturbations) as PertKey[]).forEach((k) => {
        if (next.perturbations[k].enabled) {
          next.perturbations[k] = { ...next.perturbations[k], magnitudePct: v };
        }
      });
      return next;
    });

  const togglePert = (k: PertKey, enabled: boolean) =>
    setConfig((c) => ({
      ...c,
      perturbations: {
        ...c.perturbations,
        [k]: { ...c.perturbations[k], enabled },
      },
    }));

  const setPertMagnitude = (k: PertKey, magnitudePct: number) =>
    setConfig((c) => ({
      ...c,
      perturbations: {
        ...c.perturbations,
        [k]: { ...c.perturbations[k], magnitudePct },
      },
    }));

  const activeStrength = useMemo(() => {
    const vals = (Object.keys(config.perturbations) as PertKey[])
      .filter((k) => config.perturbations[k].enabled)
      .map((k) => config.perturbations[k].magnitudePct);
    if (vals.length === 0) return null;
    const allEqual = vals.every((v) => v === vals[0]);
    if (!allEqual) return null;
    const match = (Object.keys(STRENGTH_PRESETS) as (keyof typeof STRENGTH_PRESETS)[]).find(
      (s) => STRENGTH_PRESETS[s] === vals[0]
    );
    return match ?? null;
  }, [config.perturbations]);

  const anyPertEnabled = (Object.keys(config.perturbations) as PertKey[]).some(
    (k) => config.perturbations[k].enabled
  );

  const budgetColor =
    budgetPct > 92 ? "#d32f2f" : budgetPct > 60 ? "#ff924c" : "#a78bfa";

  const handleConfirm = () => {
    if (!isEnsemble) {
      onConfirm(null);
      return;
    }
    onConfirm({ ...config, enabled: true, members: effectiveMembers });
  };

  const sectionLabel = (text: string) => (
    <Typography
      sx={{
        fontSize: "0.7rem",
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        color: "rgba(255,255,255,0.4)",
        fontWeight: 700,
        mb: 1.5,
      }}
    >
      {text}
    </Typography>
  );

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { bgcolor: "#1a1d2e", color: "#fff", borderRadius: 2 } }}
    >
      <DialogTitle
        sx={{
          m: 0,
          px: 2.5,
          py: 2,
          fontWeight: 700,
          fontSize: "1.15rem",
          borderBottom: "1px solid rgba(255,255,255,0.1)",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <HubIcon sx={{ color: "#a78bfa" }} />
        Configure Ensemble
        {isEnsemble && (
          <Chip
            size="small"
            label={`${effectiveMembers} members`}
            sx={{
              bgcolor: "rgba(167,139,250,0.18)",
              color: "#a78bfa",
              fontWeight: 600,
            }}
          />
        )}
        <IconButton
          aria-label="close"
          onClick={onClose}
          disabled={submitting}
          sx={{ position: "absolute", right: 8, top: 8, color: "rgba(255,255,255,0.7)" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ borderColor: "rgba(255,255,255,0.1)", p: 0 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1.1fr 1fr" } }}>
          {/* LEFT: run type + budget */}
          <Box sx={{ p: 2.5, borderRight: { sm: "1px solid rgba(255,255,255,0.1)" } }}>
            {sectionLabel("Run type")}
            <ToggleButtonGroup
              exclusive
              fullWidth
              size="small"
              value={isEnsemble ? "ensemble" : "single"}
              onChange={(_, v) => {
                if (v === null) return;
                setEnsembleEnabled(v === "ensemble");
              }}
              sx={{
                mb: 2.5,
                "& .MuiToggleButton-root": {
                  color: "rgba(255,255,255,0.7)",
                  borderColor: "rgba(255,255,255,0.15)",
                  textTransform: "none",
                  py: 1,
                },
                "& .Mui-selected": {
                  bgcolor: "rgba(255,107,53,0.15) !important",
                  color: "#fff !important",
                  borderColor: "#ff6b35 !important",
                },
                "& .Mui-disabled": { color: "rgba(255,255,255,0.25)" },
              }}
            >
              <ToggleButton value="single">Single run</ToggleButton>
              <ToggleButton value="ensemble" disabled={capped}>
                Ensemble
              </ToggleButton>
            </ToggleButtonGroup>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
                px: 1.5,
                py: 1,
                borderRadius: 1.5,
                bgcolor: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <Typography sx={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.7)" }}>
                Ignition regime
              </Typography>
              <Typography sx={{ fontSize: "0.82rem", fontWeight: 600 }}>
                {regimeName || "Selected regime"} · {safeIgnitionCount} pt
                {safeIgnitionCount === 1 ? "" : "s"}
              </Typography>
            </Box>

            {sectionLabel("Compute budget")}
            <Box
              sx={{
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 2,
                p: 2,
                mb: 2,
                background:
                  "linear-gradient(180deg, rgba(167,139,250,0.06), rgba(255,255,255,0.02))",
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                <Typography sx={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.7)" }}>
                  Ignitions × members
                </Typography>
                <Typography sx={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.6)" }}>
                  {Math.round(budgetPct)}% of ceiling
                </Typography>
              </Box>
              <Typography sx={{ fontSize: "1.4rem", fontWeight: 700, mb: 1 }}>
                {safeIgnitionCount.toLocaleString()} × {effectiveMembers.toLocaleString()} ={" "}
                {totalIgnitions.toLocaleString()}{" "}
                <Typography component="span" sx={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.5)" }}>
                  / {MAX_ENSEMBLE_IGNITIONS.toLocaleString()} ignitions
                </Typography>
              </Typography>
              <LinearProgress
                variant="determinate"
                value={budgetPct}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  bgcolor: "rgba(255,255,255,0.08)",
                  "& .MuiLinearProgress-bar": { bgcolor: budgetColor, borderRadius: 5 },
                }}
              />
            </Box>

            {capped ? (
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  alignItems: "flex-start",
                  bgcolor: "rgba(239,83,80,0.1)",
                  border: "1px solid rgba(239,83,80,0.3)",
                  borderLeft: "3px solid #ef5350",
                  borderRadius: 1.5,
                  p: 1.5,
                  mb: 1,
                }}
              >
                <WarningAmberIcon sx={{ color: "#ef5350", fontSize: 18, mt: "1px" }} />
                <Typography sx={{ fontSize: "0.78rem", color: "#ffd9d6", lineHeight: 1.5 }}>
                  This regime has <b>{safeIgnitionCount} ignition points</b>. Against the{" "}
                  {MAX_ENSEMBLE_IGNITIONS.toLocaleString()}-ignition compute ceiling only a{" "}
                  <b>single member</b> fits, so an ensemble adds no spread. Choose a regime with
                  fewer points (≤500) to enable an ensemble.
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  alignItems: "flex-start",
                  bgcolor: "rgba(167,139,250,0.08)",
                  border: "1px solid rgba(167,139,250,0.25)",
                  borderLeft: "3px solid #a78bfa",
                  borderRadius: 1.5,
                  p: 1.5,
                  mb: 2,
                }}
              >
                <HubIcon sx={{ color: "#a78bfa", fontSize: 18, mt: "1px" }} />
                <Typography sx={{ fontSize: "0.78rem", color: "#ddd6ff", lineHeight: 1.5 }}>
                  With {safeIgnitionCount} ignition{safeIgnitionCount === 1 ? "" : "s"} per member you
                  can run up to <b>{maxMembers.toLocaleString()} members</b> before hitting the
                  ceiling.
                </Typography>
              </Box>
            )}

            <Box sx={{ opacity: isEnsemble ? 1 : 0.4, pointerEvents: isEnsemble ? "auto" : "none" }}>
              <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.7)", mb: 0.5 }}>
                Ensemble members
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Slider
                  value={members}
                  min={1}
                  max={maxMembers}
                  onChange={(_, v) => setMembers(v as number)}
                  disabled={!isEnsemble}
                  sx={{
                    color: "#ff6b35",
                    "& .MuiSlider-thumb": { bgcolor: "#ff6b35" },
                  }}
                />
                <Box sx={{ minWidth: 96, textAlign: "right" }}>
                  <Typography sx={{ fontSize: "1.4rem", fontWeight: 700, lineHeight: 1 }}>
                    {members.toLocaleString()}
                  </Typography>
                  <Typography sx={{ fontSize: "0.66rem", color: "rgba(255,255,255,0.5)" }}>
                    of max {maxMembers.toLocaleString()}
                  </Typography>
                </Box>
              </Box>
              <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.5)", mt: 0.5 }}>
                More members → tighter confidence intervals, but more compute.
              </Typography>
            </Box>
          </Box>

          {/* RIGHT: perturbations */}
          <Box sx={{ p: 2.5, bgcolor: "rgba(0,0,0,0.18)", opacity: isEnsemble ? 1 : 0.45, pointerEvents: isEnsemble ? "auto" : "none" }}>
            {sectionLabel("Perturbations (applied per member)")}

            <ToggleButtonGroup
              exclusive
              fullWidth
              size="small"
              value={activeStrength}
              onChange={(_, v) => v && setStrength(v)}
              sx={{
                mb: 2,
                "& .MuiToggleButton-root": {
                  color: "rgba(255,255,255,0.7)",
                  borderColor: "rgba(255,255,255,0.15)",
                  textTransform: "none",
                },
                "& .Mui-selected": {
                  bgcolor: "rgba(255,107,53,0.15) !important",
                  color: "#fff !important",
                  borderColor: "#ff6b35 !important",
                },
              }}
            >
              <ToggleButton value="low">Low noise</ToggleButton>
              <ToggleButton value="med">Medium</ToggleButton>
              <ToggleButton value="high">High noise</ToggleButton>
            </ToggleButtonGroup>

            {PERT_META.map(({ key, title, desc }) => {
              const p = config.perturbations[key];
              return (
                <Box
                  key={key}
                  sx={{
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 2,
                    p: 1.5,
                    mb: 1,
                    opacity: p.enabled ? 1 : 0.5,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Switch
                      size="small"
                      checked={p.enabled}
                      onChange={(e) => togglePert(key, e.target.checked)}
                      sx={{
                        "& .Mui-checked": { color: "#ff6b35" },
                        "& .Mui-checked + .MuiSwitch-track": { bgcolor: "#ff6b35 !important" },
                      }}
                    />
                    <Typography sx={{ flex: 1, fontSize: "0.88rem", fontWeight: 600 }}>
                      {title}
                    </Typography>
                    <Typography sx={{ fontSize: "0.78rem", color: "#ff6b35", fontWeight: 700 }}>
                      {p.enabled ? `±${p.magnitudePct}%` : "off"}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.5)", my: 0.5, lineHeight: 1.45 }}>
                    {desc}
                  </Typography>
                  <Slider
                    value={p.magnitudePct}
                    min={0}
                    max={20}
                    disabled={!p.enabled}
                    onChange={(_, v) => setPertMagnitude(key, v as number)}
                    sx={{ color: "#ff6b35", "& .MuiSlider-thumb": { bgcolor: "#ff6b35" } }}
                  />
                </Box>
              );
            })}

            {!anyPertEnabled && (
              <Typography sx={{ fontSize: "0.72rem", color: "#ffb4a8", mt: 0.5 }}>
                Enable at least one perturbation channel — with none, every member is identical.
              </Typography>
            )}
          </Box>
        </Box>

        {/* Inherited weather summary */}
        {baseParams && (
          <Box sx={{ px: 2.5, py: 1.5, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
              <Typography sx={{ fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.06em", color: "rgba(255,255,255,0.4)", fontWeight: 700 }}>
                From weather setup
              </Typography>
              {baseParams.weatherLabel && (
                <SummaryItem k="Weather" v={baseParams.weatherLabel} />
              )}
              {baseParams.weather_grid && (
                <SummaryItem
                  k="Spatial"
                  v={`Varies across project · ${weatherGridShapeLabel(baseParams.weather_grid)}`}
                />
              )}
              {baseParams.startTime && (
                <SummaryItem k="Start" v={dayjs(baseParams.startTime).format("D MMM YYYY HH:mm")} />
              )}
              <SummaryItem k="Duration" v={`${baseParams.duration}h`} />
              <SummaryItem k="Drought" v={`${baseParams.droughtFactor}/10`} />
              <SummaryItem k="Curing" v={`${baseParams.curing}%`} />
              <SummaryItem k="Spotting" v={SPOTTING_MODE_LABELS[baseParams.spottingMode ?? "realistic"]} />
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: "1px solid rgba(255,255,255,0.1)" }}>
        <Typography sx={{ mr: "auto", fontSize: "0.78rem", color: "rgba(255,255,255,0.5)" }}>
          {isEnsemble
            ? `${effectiveMembers} runs · ${totalIgnitions.toLocaleString()} ignitions queued`
            : `1 run · ${safeIgnitionCount.toLocaleString()} ignition${safeIgnitionCount === 1 ? "" : "s"}`}
        </Typography>
        <Button onClick={onClose} disabled={submitting} sx={{ color: "rgba(255,255,255,0.7)" }}>
          Cancel
        </Button>
        <Tooltip title={isEnsemble && !anyPertEnabled ? "Enable a perturbation channel first" : ""}>
          <span>
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={submitting || (isEnsemble && !anyPertEnabled)}
              startIcon={<PlayArrowIcon />}
              sx={{
                bgcolor: "#ff6b35",
                fontWeight: 600,
                "&:hover": { bgcolor: "#ff7f50" },
                "&:disabled": { bgcolor: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.3)" },
              }}
            >
              {isEnsemble ? `Run ensemble (${effectiveMembers})` : "Run simulation"}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

const SummaryItem: React.FC<{ k: string; v: string }> = ({ k, v }) => (
  <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}>
    <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.5)" }}>{k}</Typography>
    <Typography sx={{ fontSize: "0.8rem", fontWeight: 500 }}>{v}</Typography>
  </Box>
);

export default EnsembleConfigModal;
